import React, { useCallback, useEffect, useLayoutEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'

import { enterBooking } from '../../../lib/booking-entry'
import { whatsappChatProps } from '../../../components/whatsapp-chat'
import { wireCoverageCheck } from '../../../lib/coverage-check'
import { AnalyticsService } from '../../../services/analytics'

// /services/<slug>: ONE WASH, ONE PAGE, AND REACT DOES NOT DRAW IT.
//
// The page is server-rendered in full by app/views/gwmc/_wash_page.html.haml,
// from seo/wash_pages.json and the live catalogue, because it exists to be
// read by search engines and answer engines as much as by people
// (docs/COMPETITOR_REVIEW_THEVALETERS_2026-10-06.md §5, "the server partial is
// not optional"). Drawing it a second time here would mean a second copy of
// the copy in the bundle, which DESIGN.md §9 is the long argument against, and
// it would cost the 250 KB budget for nothing.
//
// So this keeps the server's markup. ReactDOM.render clears #app (DESIGN.md
// §7), so the markup is taken as the bundle first runs, which is after the
// document has been parsed (the script is deferred) and before the render,
// and put back, unchanged, inside the app's <main>. Same element, same
// classes, same place: the page does not move on mount.
//
// What React adds is behaviour only: a Book link goes through enterBooking()
// so the SPA does not reload and PostHog records where it sat (`entry_source`
// wash_page_top, _included, ..., _sticky), and the WhatsApp link opens the number form on a desktop.
const SERVER = (() => {
  if (typeof document === 'undefined') return null
  const element = document.querySelector('[data-wash-page]')
  if (!element) return null
  return { path: element.getAttribute('data-wash-page'), html: element.innerHTML, title: document.title }
})()

const isPlainClick = event =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey

export const WashPage = React.memo(() => {
  const { pathname, search } = useLocation()
  const ours = SERVER && SERVER.path === pathname

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Reached without its document (an in-app route change to a wash page we
  // were not loaded on): fetch the document, which carries the page.
  useEffect(() => {
    if (!ours) window.location.assign(`${pathname}${search}`)
  }, [ours, pathname, search])

  // The phone's sticky Book bar ([data-wash-sticky], hidden by CSS from
  // 769px): shown once the Book button under the price has scrolled up out
  // of view, and not while any other Book button, or the footer, is on or
  // just below the screen. The bar is in the server's markup, so this only
  // toggles a class on it; React never redraws that markup.
  useEffect(() => {
    if (!ours || typeof IntersectionObserver === 'undefined') return undefined
    const bar = document.querySelector('[data-wash-sticky]')
    const top = document.querySelector('[data-wash-book="top"]')
    if (!bar || !top) return undefined

    const near = new Set()
    let topPassed = false
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.target === top) topPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0
        else if (entry.isIntersecting) near.add(entry.target)
        else near.delete(entry.target)
      })
      bar.classList.toggle('is-shown', topPassed && near.size === 0)
    }, { rootMargin: '0px 0px 120px 0px' })

    observer.observe(top)
    document.querySelectorAll('[data-wash-book]').forEach(el => {
      if (el !== top && !bar.contains(el)) observer.observe(el)
    })
    const footer = document.querySelector('footer')
    if (footer) observer.observe(footer)
    return () => observer.disconnect()
  }, [ours])

  // "Do we cover you?" under the price (gwmc/_coverage_check): the box asks
  // GET /coverage and shows the answer in place (lib/coverage-check).
  useEffect(() => {
    if (!ours) return undefined
    return wireCoverageCheck(document.querySelector('.wash-page [data-coverage-check]'))
  }, [ours])

  const onClick = useCallback(event => {
    const link = event.target.closest && event.target.closest('a')
    if (!link) return
    // The step-down under a dearer wash's price, however it was opened.
    if (link.dataset.stepDownFrom) {
      AnalyticsService.trackWashStepDownClicked({ from: link.dataset.stepDownFrom, to: link.dataset.stepDownTo })
    }
    if (!isPlainClick(event)) return

    if (link.dataset.bookingSource) {
      event.preventDefault()
      enterBooking(link.dataset.bookingSlug || null, link.dataset.bookingSource, {
        extra: link.dataset.bookingExtra || undefined
      })
    } else if (link.dataset.whatsapp) {
      whatsappChatProps('').onClick(event)
    }
  }, [])

  if (!ours) return null

  return (
    <>
      <Helmet title={SERVER.title} />
      {/* The server's own markup, kept rather than redrawn: see above. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="wash-page" data-wash-page={SERVER.path} onClick={onClick} dangerouslySetInnerHTML={{ __html: SERVER.html }} />
    </>
  )
})
